import { MessageBarType } from '@fluentui/react';
import { Button, buttonStylesPrimary, MessageBar, MessageDialog, Pivot } from '@h2oai/ui-kit';
import { useEffect, useRef, useState } from 'react';

import { EnvIdRequest } from '../../admin-center/gen/environment_info/natgw_pb';
import { useAdminCenterService } from '../../admin-center/hooks';
import { messageBarContainerStyles } from './common/common.styles';
import AdminTable from './components/UserManagement/AdminTable/AdminTable';
import BulkImportSidePanel from './components/UserManagement/BulkImportSidePanel/BulkImportSidePanel';
import CustomGroupsTable from './components/UserManagement/CustomGroupsTable/CustomGroupsTable';
import UMSidePanel from './components/UserManagement/UMSidePanel/UMSidePanel';
import {
  adminTableStyle,
  menuBtnStyle,
  pageTopButtonStyle,
  sidePanelStyle,
  UserManagementTopStyles,
  userTableStyle,
} from './components/UserManagement/UserManagement.styles';
import UserTable from './components/UserManagement/UserTable/UserTable';
import PageWrapper from './PageWrapper';

const FAILED_USERS_LIST_MAX_HEIGHT = '400px';

export default function UserManagement() {
  const service = useAdminCenterService();
  const [isOpen, setOpen] = useState(false);
  const [isBulkImport, setIsBulkImport] = useState(false);
  const [alertResponseMessage, setAlertResponseMessage] = useState<string | null>(null);
  const [showMessageBar, setShowMessagebar] = useState<boolean>(false);
  const [isMemberRequestSuccess, setIsMemberRequest] = useState<boolean>(false);
  const [showMessageDialog, setShowMessageDialog] = useState<boolean>(false);
  const [failedUsers, setFailedUsers] = useState<string[]>([]);

  const cachedEnvId = useRef<string | null>(null);
  const envIdPromise = useRef<Promise<string> | null>(null);
  const [envId, setEnvId] = useState(localStorage.getItem('envId') || null);
  const [refreshKey, setRefreshKey] = useState(0);

  const handleRefreshTables = async () => {
    setRefreshKey((prev) => prev + 1);
  };

  const fetchEnvId = async () => {
    const cachedValue = localStorage.getItem('envId');
    if (cachedValue) {
      setEnvId(cachedValue);
      cachedEnvId.current = cachedValue;
      return;
    }

    if (!envIdPromise.current) {
      envIdPromise.current = (async () => {
        try {
          const envIdRequest: EnvIdRequest = {};
          const envIdResponse = await service.getEnvId(envIdRequest);
          const id = envIdResponse.envId || '';
          cachedEnvId.current = id;
          return id;
        } catch (error) {
          throw error;
        }
      })();
    }

    try {
      const id = await envIdPromise.current;
      setEnvId(id);
      localStorage.setItem('envId', id);
    } catch (error) {
      envIdPromise.current = null;
      console.error('Error fetching envId:', error);
    }
  };

  useEffect(() => {
    fetchEnvId();
  }, []);

  const handleAddButtonClick = () => {
    setOpen(true);
  };
  const handleAddBulkButtonClick = () => {
    setIsBulkImport(true);
  };

  async function setAlertMessageBar(isSuccess: boolean, message: string) {
    setIsMemberRequest(isSuccess);
    setAlertResponseMessage(message);
    setShowMessagebar(true);
    setIsBulkImport(false);
    setOpen(false);
    await handleRefreshTables();
  }

  function setFailedUsersDialog(failedUsers: string[]) {
    setShowMessageDialog(true);
    setFailedUsers(failedUsers);
    setIsBulkImport(false);
  }

  const emailListContainerStyles = {
    display: 'flex' as const,
    flexDirection: 'column' as const,
    gap: 6,
    maxHeight: FAILED_USERS_LIST_MAX_HEIGHT,
    overflowY: 'auto' as const,
  };

  const emailListItemStyles = {
    fontFamily: 'monospace',
  };

  return (
    <PageWrapper>
      {showMessageBar && (
        <div style={messageBarContainerStyles}>
          <MessageBar
            timeout={4000}
            onDismiss={() => setShowMessagebar(false)}
            messageBarType={isMemberRequestSuccess ? MessageBarType.success : MessageBarType.error}
          >
            {alertResponseMessage}
          </MessageBar>
        </div>
      )}
      <MessageDialog
        hidden={!showMessageDialog}
        onDismiss={() => setShowMessageDialog(false)}
        title="Failed to Add Some Users"
        content={
          <>
            <p>Failed to add following member(s). Please check the formatting and try again.</p>
            <div style={emailListContainerStyles}>
              {failedUsers.map((user) => (
                <li style={emailListItemStyles} key={user}>
                  {user}
                </li>
              ))}
            </div>
          </>
        }
      />
      <div style={UserManagementTopStyles}>
        <div style={pageTopButtonStyle}>
          <Button
            styles={buttonStylesPrimary}
            text="Bulk Import"
            onClick={handleAddBulkButtonClick}
            style={menuBtnStyle}
          />
          <Button styles={buttonStylesPrimary} text="Add Member" onClick={handleAddButtonClick} style={menuBtnStyle} />
        </div>
        {isOpen && (
          <div style={sidePanelStyle}>
            <UMSidePanel isOpen={isOpen} setOpen={setOpen} setAlertNotification={setAlertMessageBar} envId={envId} />
          </div>
        )}
        {isBulkImport && (
          <div style={sidePanelStyle}>
            <BulkImportSidePanel
              isBulkImport={isBulkImport}
              setIsBulkImport={setIsBulkImport}
              setAlertNotification={setAlertMessageBar}
              setResponseDialog={setFailedUsersDialog}
              envId={envId}
            />
          </div>
        )}
      </div>
      <Pivot
        items={[
          {
            headerText: 'Users',
            content: (
              <div style={userTableStyle}>
                <UserTable envId={envId} refreshKey={refreshKey} onSubmitSuccess={handleRefreshTables} />
              </div>
            ),
          },
          {
            headerText: 'Admins',
            content: (
              <div style={adminTableStyle}>
                <AdminTable envId={envId} refreshKey={refreshKey} onSubmitSuccess={handleRefreshTables} />
              </div>
            ),
          },
          {
            headerText: 'Custom Groups',
            content: (
              <div style={userTableStyle}>
                <CustomGroupsTable refreshKey={refreshKey} onSubmitSuccess={handleRefreshTables} />
              </div>
            ),
          },
        ]}
      />
    </PageWrapper>
  );
}
